import { useEffect, useState } from 'react';
import {
  fetchRemoteBuildId,
  hasUnsavedWork,
  noteUserEdit,
  reloadForAppUpdate,
  versionJsonUrl,
  watchAppUpdate,
} from '@cer-autofill/shared';
import { buildId } from '../env.js';

/**
 * Quiet prompt when a deploy has landed while this tab is open.
 * Reload is the person's choice, including while a form is open. The field
 * worker is asked to update on the same timer; it installs and waits so
 * precached lazy chunks stay available until Reload posts SKIP_WAITING.
 */
export function AppUpdateNotice() {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const onEdit = (event: Event) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        noteUserEdit();
      }
    };
    document.addEventListener('input', onEdit, true);
    document.addEventListener('change', onEdit, true);
    return () => {
      document.removeEventListener('input', onEdit, true);
      document.removeEventListener('change', onEdit, true);
    };
  }, []);

  useEffect(() => {
    return watchAppUpdate({
      runningBuildId: buildId,
      hasUnsavedWork,
      fetchRemoteBuildId: () =>
        fetchRemoteBuildId({ url: versionJsonUrl(import.meta.env.BASE_URL) }),
      onServiceWorkerCheck: checkServiceWorkerForUpdate,
      onDecision: (decision) => {
        if (decision.showBanner) setAvailable(true);
      },
    });
  }, []);

  if (!available) return null;

  return (
    <p className="app-update" role="status">
      A new version is available,{' '}
      <button type="button" onClick={() => void reloadForUpdate()}>
        Reload
      </button>
    </p>
  );
}

async function checkServiceWorkerForUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.update();
  } catch {
    // Offline. The precached shell keeps working; the banner waits for a network.
  }
}

function reloadForUpdate(): Promise<void> {
  return reloadForAppUpdate({
    reload: () => window.location.reload(),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (id) => window.clearTimeout(id as number),
    ...('serviceWorker' in navigator
      ? {
          serviceWorker: {
            addEventListener: (type, listener, options) => {
              navigator.serviceWorker.addEventListener(type, listener, options);
            },
            getRegistration: () => navigator.serviceWorker.getRegistration(),
          },
        }
      : {}),
  });
}
